<template>
  <banners
    :sticky-top="true"
    :narrow="true"
    :is-ultra="true"
  >
    <Layout
      :title="title"
      :loads="loads"
      :is-desktop="isDesktop"
      :is-loading="isLoading"
      :is-loads-refetching="isLoadsRefetching"
      @search-loads="searchLoads"
      @fetch-more="fetchMoreLoads"
      @select-page="selectPage"
    ></Layout>
  </banners>
</template>

<script setup>
import { ref, computed, onBeforeMount, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Banners from '@/components/Banners.vue';
import Layout from '@/components/loads/Layout.vue';
import { loadQuery, createFilterQuery } from '../../utils/loadFilters.js';
import { useScreenSize } from '@/composables/useScreenSize';
import { useExchangeAccess } from '@/composables/useExchangeAccess';
import CargoPolicyNotification from '@/components/loads/CargoPolicyNotification.vue';

const store = useStore();
const router = useRouter();
const { screenSize } = useScreenSize();
const { showCargoPolicyBanner } = useExchangeAccess();

const isLoadsRefetching = ref(false);

const isLoading = computed(() => store.state.loads?.isLoadsLoading);

const isDesktop = computed(() => screenSize.value === 'desktop');

const title = computed(() => 'Find Loads');

const currentPage = computed(
  () => store.state.loads?.loadboard_pagination?.currentPage
);

const totalPages = computed(
  () => store.state.loads?.loadboard_pagination?.totalPages
);

const getLoads = async ({ query = '', refetch = false, page = 1 }) => {
  const sort = router.currentRoute?.value?.query?.sort || '';
  try {
    await store.dispatch('loads/searchLoads', {
      query,
      sort,
      refetch,
      page,
      // status: currentTab.value,
    });
  } catch (error) {
    console.error('Error fetching loads: ', error);
  } finally {
    store.commit('loads/setLoading', false);
  }
};

const addFiltersFromUrl = (values) => {
  let sort = 'start_stop_scheduled_window_started_at+asc';

  getLoads({ sort, query: values });
};

const searchFilters = computed(() => store.state.loads?.loadSearchFilters);

const initialLoadSeach = ref(false);

onBeforeMount(async () => {
  store.commit('loads/setLoading', true);

  const existingQuery = router.currentRoute?.value?.query;
  const existingSort = existingQuery?.sort || '';

  let loadSearchFilterStorage = {};
  // const existingFilters = Object.entries(searchFilters?.value)
  //   ? searchFilters?.value
  //   : loadSearchFilterStorage;

  let origin;
  let destination;
  // maintain origin/destination from storage

  if (existingQuery?.company_id) {
    await store.dispatch('brokers/getCompanyById', existingQuery?.company_id);
  } else {
    loadSearchFilterStorage =
      JSON.parse(localStorage.getItem('load_search_filters')) || {};
  }

  if (existingQuery?.origin) {
    if (loadSearchFilterStorage?.origin) {
      delete existingQuery.origin;
    }
  }

  if (existingQuery?.destination) {
    if (loadSearchFilterStorage?.destination) {
      delete existingQuery.destination;
    }
  }

  const c = { ...loadSearchFilterStorage, ...existingQuery };
  const combined = {
    ...c,
    ...(origin ? { origin_area_id: origin } : {}),
    ...(destination ? { destination_area_id: destination } : {}),
    ...(c.trailer
      ? { trailer: Array.isArray(c.trailer) ? c.trailer : [c.trailer] }
      : {}),
    ...(c.posting
      ? { posting: Array.isArray(c.posting) ? c.posting : [c.posting] }
      : {}),
    ...(c.query ? { searchQuery: c.query } : {}),
  };

  if (Object.entries(searchFilters?.value)) {
    await store.commit('loads/setLoadSearchFilters', combined);
  }

  const combinedQuery = createFilterQuery(combined, existingQuery);

  if (Object.entries(combinedQuery)?.length > 0) {
    initialLoadSeach.value = true;
  }

  if (
    Object.entries(store.state.loads?.loadSearchFilters)?.length > 0 &&
    store.state.loads?.loads?.results?.length > 0
  ) {
    // Already has load index data, and filters haven't changed
    store.commit('loads/setLoading', false);
  } else {
    await store.dispatch('loads/searchLoads', {
      query: combined,
      sort: existingSort,
    });
  }

  await router.push({
    query: {
      ...combinedQuery,
    },
  });

  setTimeout(() => {
    // Fallback
    initialLoadSeach.value = false;
  }, 1500);
});

watch(
  () => router.currentRoute.value.query,
  (newValue) => {
    if (initialLoadSeach.value) {
      initialLoadSeach.value = false;
    } else {
      let queries = { ...newValue };
      if (newValue?.equipment && !Array.isArray(newValue?.equipment)) {
        queries.equipment = [newValue.equipment];
      } else if (newValue?.trailer && !Array.isArray(newValue?.trailer)) {
        queries.trailer = [newValue.trailer];
      }

      if (router.currentRoute.value?.name === 'Load Board') {
        addFiltersFromUrl(searchFilters.value);
      }
    }
  },
  { deep: true }
);

const loads = computed(() => store.state.loads?.loads?.results);

const searchLoads = async (term) => {
  await store.dispatch('loads/getLoads', {
    query: `${loadQuery['dispatch']}&q[single_external_load_number_equals]=${term}`,
  });
};

const fetchMoreLoads = async () => {
  if (!isLoading.value && !isDesktop?.value) {
    console.log('Hit infinite scroll');
    if (currentPage?.value !== totalPages?.value) {
      isLoadsRefetching.value = true;

      // re-fetch loads based on page. Add to existing array
      await getLoads({
        page: +currentPage.value + 1,
        query: searchFilters?.value,
        refetch: true,
      });

      isLoadsRefetching.value = false;
    }
  }
};

const selectPage = async (page) => {
  if (page != currentPage?.value) {
    await getLoads({
      page,
      query: searchFilters?.value,
    });
  }
};

onMounted(async () => {
  if (showCargoPolicyBanner.value) {
    await store.dispatch('toast/addToast', {
      component: CargoPolicyNotification,
      props: {
        visible: true,
        title: 'Your cargo policy is not on file',
      },
      duration: 15000,
    });
  }
});
</script>
